import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ShareEntityButton, { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import ToasterContext, { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import requestInvoice from "@solveindustrial/Store/Components/InvoiceDetailHeader/Handlers/RequestInvoice";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}
// VERNDALE CUSTOMIZATION START
export interface OwnProps extends WidgetProps {
    fields: {
        [fields.generateEmailAttachmentFromWebpage]: boolean;
    };
}

// VERNDALE CUSTOMIZATION END

const mapDispatchToProps = {
    requestInvoice, // VERNDALE CUSTOMIZATION
};

type Props = OwnProps & WidgetProps & ResolveThunks<typeof mapDispatchToProps> & HasToasterContext; // VERNDALE CUSTOMIZATION

export interface InvoiceDetailHeaderStyles {
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    printButton?: ButtonPresentationProps;
    downloadButton?: ButtonPresentationProps; // Verndale Custom Feature
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    emailClickable?: ClickableProps;
    downloadClickable?: ClickableProps; // Verndale Custom Feature
    spinner?: LoadingSpinnerProps; // Verndale Custom Feature
}

export const headerStyles: InvoiceDetailHeaderStyles = {
    shareEntityButtonStyles: {
        button: {
            buttonType: "outline",
            variant: "secondary",
        },
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 1, 6, 6],
    },
    titleGridItem: {
        width: [11, 11, 11, 6, 6],
    },
    title: {
        variant: "h2",
        as: "h1",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },

    downloadClickable: {}, // Verndale Custom Feature
    downloadButton: {}, // Verndale Custom Feature
    spinner: {
        css: css`
            margin: auto;
            height: 30px;
            width: 30px;
        `,
    },  // Verndale Custom Feature
};

const styles = headerStyles;
const InvoiceDetailHeader = ({ fields, requestInvoice, toaster }: Props) => {
    const { value: invoice } = useContext(InvoiceStateContext);

    const [isDownloading, setIsDownloading] = React.useState(false); // Verndale Custom Feature
    const [dowloadFailed, setDownloadFailed] = React.useState(false); // Verndale Custom Feature
    const toasterContext = React.useContext(ToasterContext); // Verndale Custom Feature

    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    if (!invoice) {
        return null;
    }

    const printLabel = translate("Print");

    // Verndale Custom Feature = START

    const handleDownload = () => {
        setIsDownloading(true);

        requestInvoice({
            CustomerNumber: invoice.customerNumber,
            DocumentType: "Invoice",
            DocumentNumber: invoice.invoiceNumber,
            onComplete: result => {
                if (result.apiResult) {
                    const linkSource = `data:application/pdf;base64,${result.apiResult.reply.encodedDocument}`;
                    const downloadLink = document.createElement("a");
                    const fileName = `${invoice.invoiceNumber}.pdf`;
                    downloadLink.href = linkSource;
                    downloadLink.download = fileName;
                    downloadLink.click();
                    downloadLink.remove();

                    toasterContext.addToast({
                        body: translate("Success! The invoice has been downloaded."),
                        messageType: "success",
                    });

                    setIsDownloading(false);
                } else {
                    /* toasterContext.addToast({
                        body: translate("An error occurred while submitting your request."),
                        messageType: "danger",
                    }); */
                    setIsDownloading(false);
                    setDownloadFailed(true);
                }
            },
        });
    };

    // Verndale Custom Feature - END

    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.title}>{`${translate("Invoice")} ${invoice.invoiceNumber}`}</Typography>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Hidden {...styles.menuHiddenContainer}>
                    <OverflowMenu hasChildPortal position="end" {...styles.narrowOverflowMenu}>
                        <ShareEntityButton
                            entityId={invoice.invoiceNumber}
                            variant="clickable"
                            entityName="Invoice"
                            extendedStyles={styles.shareEntityButtonStyles}
                            generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                        />
                        {/* Verndale Custom Feature */}

                        <Clickable
                            onClick={handleDownload}
                            {...styles.downloadClickable}
                            data-test-selector="invoiceDetails_download"
                            disabled={isDownloading || dowloadFailed}
                        >
                            {isDownloading ? <LoadingSpinner {...styles.spinner} /> : translate("Download")}
                        </Clickable>

                        {/* Verndale Custom Feature */}
                        <Clickable
                            {...styles.printClickable}
                            onClick={openPrintDialog}
                            data-test-selector="invoiceDetails_print"
                        >
                            {printLabel}
                        </Clickable>
                    </OverflowMenu>
                </Hidden>
                <Hidden {...styles.buttonsHiddenContainer}>
                    <ShareEntityButton
                        entityId={invoice.invoiceNumber}
                        entityName="Invoice"
                        extendedStyles={styles.shareEntityButtonStyles}
                        generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                    />
                    {/* Verndale Custom Feature */}

                    <Button
                        {...styles.downloadButton}
                        onClick={handleDownload}
                        data-test-selector="invoiceDetails_download"
                        disabled={isDownloading || dowloadFailed}
                    >
                        {isDownloading ? <LoadingSpinner {...styles.spinner} /> : translate("Download")}
                    </Button>

                    {/* Verndale Custom Feature */}
                    <Button {...styles.printButton} onClick={openPrintDialog} data-test-selector="invoiceDetails_print">
                        {printLabel}
                    </Button>
                </Hidden>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(InvoiceDetailHeader) as any,
    definition: {
        displayName: "Page Header",
        allowedContexts: ["InvoiceDetailsPage"],
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
